import { Button, CloseButton, SettingsButton, SaveButton } from '@ui/shared/Button';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import {
  IWeightReportSettings,
  weightReportSettingsProvider,
} from '../../../../data/providers/settings/reports/weightReportSettingsProvider';
import { updateWeightSettings } from '../../../../data/writers/settings/reports/weightReportSettings';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { WeightForm } from './Form';

interface IPopupProps {
  onClose: () => void;
  onSave: () => void;
}

export const Popup = (props: IPopupProps): React.ReactElement => {
  const [settings, setSettings] = React.useState<IWeightReportSettings>();
  const [initialSettings, setInitialSettings] =
    React.useState<IWeightReportSettings>();
  const [loading, setLoading] = React.useState<boolean>(true);

  /**
   * @description subscribe to trend report settings
   */
  React.useEffect((): (() => void) => {
    const subscriber = weightReportSettingsProvider().subscribe(
      (mSettings: IWeightReportSettings): void => {
        setSettings(mSettings);
        setInitialSettings(mSettings);
        setLoading(false);
      }
    );

    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description saves the change to database
   */
  const handleSave = async (): Promise<void> => {
    /// only go into loading state if settings actually changed
    if (!isEqual(initialSettings, settings)) {
      props.onSave();
    }

    setLoading(true);
    await updateWeightSettings(settings);
    props.onClose();
  };

  /**
   * @description handles changes to settings
   * @param mSettings
   * @returns
   */
  const changeHandler = (mSettings: IWeightReportSettings): void =>
    setSettings(mSettings);

  return (
    <PopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>Weight Report Settings</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {loading && <LinearLoading />}
          <WeightForm settings={settings} onSettingsChange={changeHandler} />
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton onClick={handleSave} disabled={loading} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  style?: React.CSSProperties;
  onSave: () => void;
}
export const WeightSettings = withRouter(
  (props: IProps): React.ReactElement => {
    const open = (): void =>
      openPopup(
        <Popup onClose={closePopup(props.history)} onSave={props.onSave} />,
        props.history,
        'trend-report-settings'
      );

    return <SettingsButton onClick={open} style={props.style} />;
  }
);
